import { useContext } from "react";
import { collection, addDoc } from "firebase/firestore";
import { db } from "../config/firebase";
import { SchoolContext } from "../config/SchoolContext";
import { useAuth } from "../config/AuthContext";
import useSchoolDropdown from "../components/SchoolUsers/hooks/useSchoolDropdown";
import { formatDate } from "../utils/formatDate";

/**
 * Custom hook for logging course planning activities
 * @returns {Object} Object containing the logEvent function
 */
const useCoursePlanningLogger = () => {
  const { schoolId } = useContext(SchoolContext);
  const { currentUser } = useAuth();
  const { data: schools } = useSchoolDropdown();

  /**
   * Get school name from schoolId
   * @param {string} targetSchoolId - Optional school ID, defaults to current context schoolId
   * @returns {string} School name or "Unknown School"
   */
  const getSchoolName = (targetSchoolId = schoolId) => {
    if (!schools || !targetSchoolId) return "Unknown School";
    // AdminSchoolsCollection (this hook's `schools`) returns school_id as a
    // string, but the district school picker (DistrictSchoolsList) sets
    // schoolId from DistrictSchools/{district}, which returns it as a
    // number — a strict === here silently fails for every district-picked
    // school, not just one district, so compare as strings.
    const school = schools.find(
      (s) => String(s.school_id) === String(targetSchoolId),
    );
    return school ? school.school_name : "Unknown School";
  };

  /**
   * Sanitize data for Firestore by converting dayjs objects and other non-serializable objects
   * @param {any} data - Data to sanitize
   * @returns {any} Sanitized data safe for Firestore
   */
  const sanitizeDataForFirestore = (data) => {
    if (!data) return data;

    // Handle arrays
    if (Array.isArray(data)) {
      return data.map((item) => sanitizeDataForFirestore(item));
    }

    // Handle objects
    if (typeof data === "object" && data !== null) {
      // Check if it's a dayjs object
      if (
        data._isAMomentObject ||
        data.$isDayjsObject ||
        (data.toISOString && typeof data.toISOString === "function")
      ) {
        return data.toISOString();
      }

      // Handle regular objects
      const sanitized = {};
      for (const [key, value] of Object.entries(data)) {
        sanitized[key] = sanitizeDataForFirestore(value);
      }
      return sanitized;
    }

    // Return primitive values as-is
    return data;
  };

  /**
   * Log an event to the CoursePlanningLog collection
   * @param {string} type - Type of event: "Addition", "Update", "Deletion", "View"
   * @param {string} description - Description of what happened
   * @param {Object} rowData - Optional: Current row data
   * @param {Object} previousData - Optional: Previous row data (for updates)
   * @param {string} targetSchoolId - Optional: Specific school ID (defaults to context schoolId)
   * @returns {Promise<void>}
   */
  const logEvent = async (
    type,
    description,
    rowData = null,
    previousData = null,
    targetSchoolId = schoolId,
  ) => {
    try {
      const schoolName = getSchoolName(targetSchoolId);
      const logEntry = {
        schoolId: targetSchoolId || schoolId,
        schoolName: schoolName,
        type: type, // "Addition", "Update", "Deletion", "View"
        description: description,
        rowData: sanitizeDataForFirestore(rowData),
        previousData: sanitizeDataForFirestore(previousData),
        timestamp: new Date().toISOString(),
        userId: currentUser?.uid || "Unknown User",
        userEmail: currentUser?.email || "Unknown Email",
      };

      await addDoc(collection(db, "CoursePlanningLog"), logEntry);
      console.log(`Course Planning Log: ${type} - ${description}`);
    } catch (error) {
      console.error("Error logging course planning event:", error);
      // Don't throw the error to avoid breaking the main functionality
    }
  };

  /**
   * Log a form view event
   * @param {number} recordCount - Number of existing records loaded
   * @param {string} targetSchoolId - Optional: Specific school ID
   */
  const logFormView = async (recordCount = 0, targetSchoolId = schoolId) => {
    await logEvent(
      "View",
      `Viewed course planning form with ${recordCount} existing records`,
      null,
      null,
      targetSchoolId,
    );
  };

  /**
   * Log a new course planning entry
   * @param {Object} rowData - The new row data
   * @param {string} targetSchoolId - Optional: Specific school ID
   */
  const logAddition = async (rowData, targetSchoolId = schoolId) => {
    await logEvent(
      "Addition",
      `Added new course planning entry: ${
        rowData.courseName || "Unnamed Course"
      }`,
      rowData,
      null,
      targetSchoolId,
    );
  };

  /**
   * Log an update to an existing course planning entry
   * @param {string} field - Field that was updated
   * @param {any} newValue - New value
   * @param {any} previousValue - Previous value
   * @param {Object} rowData - Current row data
   * @param {Object} previousRowData - Previous row data
   * @param {string} targetSchoolId - Optional: Specific school ID
   */
  const logUpdate = async (
    field,
    newValue,
    previousValue,
    rowData,
    previousRowData,
    targetSchoolId = schoolId,
  ) => {
    await logEvent(
      "Update",
      `Updated ${field} from "${previousValue}" to "${newValue}" for course: ${
        rowData.courseName || "Unnamed Course"
      }`,
      rowData,
      previousRowData,
      targetSchoolId,
    );
  };

  /**
   * Log deletion of a course planning entry
   * @param {Object} deletedRowData - The deleted row data
   * @param {string} targetSchoolId - Optional: Specific school ID
   */
  const logDeletion = async (deletedRowData, targetSchoolId = schoolId) => {
    await logEvent(
      "Deletion",
      `Deleted course planning entry: ${
        deletedRowData.courseName || "Unnamed Course"
      }`,
      deletedRowData,
      null,
      targetSchoolId,
    );
  };

  /**
   * Log bulk updates to an existing course planning entry (called on save)
   * @param {Array} changedFields - Array of changed field objects with field, oldValue, newValue
   * @param {Object} rowData - Current row data
   * @param {Object} previousRowData - Previous row data
   * @param {string} targetSchoolId - Optional: Specific school ID
   */
  const logBulkUpdate = async (
    changedFields,
    rowData,
    previousRowData,
    targetSchoolId = schoolId,
  ) => {
    if (!changedFields || changedFields.length === 0) return;

    const changeDescription = changedFields
      .map(
        (change) =>
          `${change.field}: "${change.oldValue}" → "${change.newValue}"`,
      )
      .join(", ");

    await logEvent(
      "Update",
      `Updated ${changedFields.length} field(s) for course "${
        rowData.courseName || "Unnamed Course"
      }": ${changeDescription}`,
      rowData,
      previousRowData,
      targetSchoolId,
    );
  };

  /**
   * Log a change request submission
   * @param {Object} changeRequestData - The change request data
   * @param {string} targetSchoolId - Optional: Specific school ID
   */
  const logChangeRequest = async (
    changeRequestData,
    targetSchoolId = schoolId,
  ) => {
    try {
      const fieldChanges = changeRequestData.fieldChanges || {};

      // Helper function to format dates for display
      const formatDisplayValue = (value, fieldName) => {
        // Check if this looks like an ISO date string for date-related fields
        if (
          (fieldName.includes("Date") || fieldName.includes("date")) &&
          typeof value === "string" &&
          value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)
        ) {
          try {
            return formatDate(value) || value;
          } catch (error) {
            return value; // Return original if formatting fails
          }
        }
        return value;
      };

      const changedFieldsArray = Object.keys(fieldChanges).map((fieldName) => ({
        field: fieldName,
        oldValue: formatDisplayValue(
          fieldChanges[fieldName].originalValue,
          fieldName,
        ),
        newValue: formatDisplayValue(
          fieldChanges[fieldName].newValue,
          fieldName,
        ),
      }));

      const changeDescription =
        changedFieldsArray.length > 0
          ? changedFieldsArray
              .map(
                (change) =>
                  `${change.field}: "${change.oldValue}" → "${change.newValue}"`,
              )
              .join(", ")
          : "No specific changes provided";

      await logEvent(
        "Change Request",
        `Submitted change request for course "${
          changeRequestData.courseName || "Unnamed Course"
        }": ${changeDescription}`,
        changeRequestData,
        null,
        targetSchoolId,
      );
    } catch (error) {
      console.error("Error logging change request:", error);
    }
  };

  return {
    logEvent,
    logFormView,
    logAddition,
    logUpdate,
    logDeletion,
    logBulkUpdate,
    logChangeRequest,
    getSchoolName,
  };
};

export default useCoursePlanningLogger;
